import { firstPersonIn, firstPersonNarrator, povFromText, settingConflicts, type IgnoredTerm, type SettingConflict, type StoryCtx, type StoryNode, type ThoughtLink } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { placeLinksAt, type PlaceLinkAt } from './placeShift';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';

/**
 * Text gegen Angabe von Hand (PF 56, core `settingConflicts`): Schauplatz bzw. Perspektive der Szene sind anders gesetzt, als der Draft
 * sagt – die Stelle (die erste Ortsnennung bzw. der erste Gedanke) ist rot wie eine Zeitmarke, die nicht mehr stimmt (`suggest.ts`, Art
 * `conflict`). ✓ übernimmt die Angabe aus dem Text, ✕ merkt „ist Absicht“ im Dokument. Ebenso rot: jeder Gedanke einer anderen Person
 * neben denen der Perspektivfigur (Head-Hopping, PF 26), nur mit ✕. Dazu gelb die Perspektive aus dem Text (PF 50, core `povFromText`):
 * keine gesetzt, Gedanken nur einer Person – ✓ macht sie zur Perspektive, ✕ merkt „ohne Perspektive“. Nur im Draft einer Szene.
 */

/** Ein Gedanke im Editor: wer ihn denkt und wo er steht */
export type ThoughtAt = ThoughtLink & { from: number; to: number };

export type SettingAct = SuggestAct;

export interface SettingHit {
  from: number;
  to: number;
  conflict: SettingConflict<PlaceLinkAt, ThoughtAt>;
}

export interface SettingLive extends SuggestLive<SettingHit> {
  ctx: StoryCtx;
  scene: StoryNode | null;
  /** Element zu einem Verweisschlüssel */
  entityOf: (key: string) => string | null;
  /** Eintrag zu einem Verweisschlüssel, wenn er ein Ort ist */
  placeOf: (key: string) => string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: SettingHit) => string;
  hitLabels: (hit: SettingHit) => { accept: string | null; ignore: string };
}

export const settingConflictKey = new PluginKey<SuggestState<SettingHit>>('settingConflicts');

/** Gedanken im Dokument (Bezüge der Art „Gedanke“) in Textreihenfolge; ein Bezug über mehrere Textstücke zählt einmal */
export function thoughtsAt(doc: PMNode, entityOf: (key: string) => string | null): ThoughtAt[] {
  const out: ThoughtAt[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const span = node.marks.find((m) => m.type.name === 'refSpan' && m.attrs.role === 'gedanke');
    if (!span) return false;
    const all = span.attrs.keys as string[];
    const last = out[out.length - 1];
    if (last && last.to === pos && last.keys.join(' ') === all.filter((k) => entityOf(k)).join(' ')) {
      last.to = pos + node.nodeSize;
      return false;
    }
    const keys = all.filter((k) => entityOf(k));
    out.push({ keys, entityIds: keys.map((k) => entityOf(k)!), from: pos, to: pos + node.nodeSize });
    return false;
  });
  return out;
}

function settingHits(doc: PMNode, l: SettingLive): SettingHit[] {
  if (!l.scene) return [];
  return settingConflicts(l.ctx, l.scene, placeLinksAt(doc, l.placeOf), thoughtsAt(doc, l.entityOf), l.ignored).map((conflict) => ({ from: conflict.at.from, to: conflict.at.to, conflict }));
}

export const settingConflictMarks = (live: { current: SettingLive }) =>
  suggestExtension<SettingHit, SettingLive>(
    {
      name: 'settingConflicts',
      kind: 'conflict',
      key: settingConflictKey,
      base: 'rt-conflict',
      hits: settingHits,
      mark: (h, l) => ({ class: `rt-setting-conflict ${h.conflict.kind}`, 'data-setting-conflict': h.conflict.kind, title: l.title(h) }),
      labels: (h, l) => l.hitLabels(h),
      word: (h) => (h.conflict.kind === 'place' ? h.conflict.at.key : h.conflict.at.keys.join(' ')),
      testId: 'setting',
    },
    live,
  );

/** Perspektive aus dem Text (PF 50): die vorgeschlagene Person am ersten ihrer Gedanken bzw. (Ich-Form) am ersten „ich“ der Erzählung */
export interface PovHit {
  from: number;
  to: number;
  entityId: string;
  key: string;
  firstPerson?: boolean;
}

export interface PovLive extends SuggestLive<PovHit> {
  ctx: StoryCtx;
  scene: StoryNode | null;
  entityOf: (key: string) => string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: PovHit) => string;
}

export const povSuggestKey = new PluginKey<SuggestState<PovHit>>('povSuggest');

/** Das erste „ich“ der Erzählung im Dokument: außerhalb von Rede und Gedanken (Bezüge) und Anführungszeichen */
function firstPersonAt(doc: PMNode): { from: number; to: number } | null {
  let found: { from: number; to: number } | null = null;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (!node.isTextblock) return true;
    let text = '';
    node.forEach((child) => {
      const t = child.isText ? (child.text ?? '') : ' '.repeat(child.nodeSize);
      const spoken = child.marks.some((m) => m.type.name === 'refSpan' && (m.attrs.role === 'rede' || m.attrs.role === 'gedanke'));
      text += spoken ? ' '.repeat(t.length) : t;
    });
    const at = firstPersonIn(text);
    if (at) found = { from: pos + 1 + at.from, to: pos + 1 + at.to };
    return false;
  });
  return found;
}

function povHits(doc: PMNode, l: PovLive): PovHit[] {
  if (!l.scene) return [];
  const s = povFromText(l.ctx, l.scene, thoughtsAt(doc, l.entityOf), l.ignored);
  if (s) return [{ from: s.at.from, to: s.at.to, entityId: s.entityId, key: s.at.keys[s.at.entityIds.indexOf(s.entityId)] ?? '' }];
  // Ich-Form: der Ich-Erzähler der übrigen Szenen, am ersten „ich“
  const narrator = firstPersonNarrator(l.ctx, l.scene, l.ignored);
  const at = narrator ? firstPersonAt(doc) : null;
  return narrator && at ? [{ ...at, entityId: narrator, key: l.ctx.entities.get(narrator)?.refKey ?? '', firstPerson: true }] : [];
}

export const povSuggestMarks = (live: { current: PovLive }) =>
  suggestExtension<PovHit, PovLive>(
    {
      name: 'povSuggest',
      kind: 'pov',
      key: povSuggestKey,
      hits: povHits,
      mark: (h, l) => ({ class: 'rt-pov-suggest', 'data-pov-suggest': h.key, title: l.title(h) }),
      word: (h) => h.key,
      testId: 'pov',
    },
    live,
  );
